import React, {useEffect, useMemo, useState} from "react";
import {getJSON} from "../api.js";
import {Badge} from "./Badge.jsx";
import {LoadingState, ErrorState} from "./LoadingState.jsx";
import {PnlCharts} from "./PnlCharts.jsx";
import {SortHeader, sortRows} from "./SortButton.jsx";
import {TradesTable} from "./TradesTable.jsx";
import {formatDateTime, metricClass, money, signed} from "../format.js";

function ContributorsTable({contributors}) {
    const [sort, setSort] = useState({field: "", direction: "desc"});
    const ordered = useMemo(() => sortRows(contributors || [], sort, (row, field) => row[field] ?? null), [contributors, sort]);
    const onSort = (field) => setSort((current) => ({
        field,
        direction: current.field === field ? (current.direction === "asc" ? "desc" : "asc") : ["strategyRef", "comparison"].includes(field) ? "asc" : "desc",
    }));
    return (
        <div className="table-wrap">
            <table>
                <thead>
                    <tr>
                        <SortHeader
                            label="Strategy" field="strategyRef" sort={sort}
                            onSort={onSort}
                        />
                        <SortHeader
                            label="Net P&L" field="netPnl" sort={sort}
                            onSort={onSort} numeric
                        />
                        <SortHeader
                            label="Risk" field="riskUsd" sort={sort}
                            onSort={onSort} numeric
                        />
                        <SortHeader
                            label="Trades" field="tradeCount" sort={sort}
                            onSort={onSort} numeric
                        />
                        <SortHeader
                            label="Status" field="comparison" sort={sort}
                            onSort={onSort}
                        />
                    </tr>
                </thead>
                <tbody>
                    {ordered.map((row) => (
                        <tr className="clickable" key={row.strategyRef}>
                            <td><a className="mono" href={`#strategy/${encodeURIComponent(row.strategyRef)}`}>{row.strategyRef}</a></td>
                            <td className={`num ${metricClass(row.netPnl)}`}>{signed(row.netPnl)}</td>
                            <td className="num">{money(row.riskUsd)}</td>
                            <td className="num">{row.tradeCount}</td>
                            <td><Badge value={row.comparison}/></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Stat({label, value, className = ""}) {
    return (
        <div className="stat">
            <div className="metric-label">{label}</div>
            <div className={`metric ${className}`}>{value}</div>
        </div>
    );
}

export function PortfolioDetail({id, analysisWindow, refreshKey, onBack, onRefreshed}) {
    const [state, setState] = useState({loading: true, error: null, data: null});
    useEffect(() => {
        let cancelled = false;
        setState((current) => current.data?.id === id
            ? {...current, loading: false, error: null}
            : {loading: true, error: null, data: null, metadata: []});
        getJSON(`/api/portfolios/${encodeURIComponent(id)}?window=${encodeURIComponent(analysisWindow)}`)
            .then((data) => {
                if (!cancelled) {
                    setState({loading: false, error: null, data});
                    onRefreshed(`Snapshot refreshed · ${data.window} analysis · ${data.placements.length} placements`);
                }
            })
            .catch((error) => {
                if (!cancelled) setState({loading: false, error, data: null});
            });
        return () => {
            cancelled = true;
        };
    }, [id, analysisWindow, refreshKey, onRefreshed]);

    if (state.loading) return <LoadingState/>;
    if (state.error) return <ErrorState error={state.error}/>;
    const portfolio = state.data;
    return (
        <>
            <section className="section">
                <div className="detail-header">
                    <div>
                        <div className="eyebrow">Portfolio</div>
                        <div className="detail-title">
                            <h2>{portfolio.name}</h2>
                            <Badge value={portfolio.health}/>
                        </div>
                        <p className="detail-copy">{portfolio.description}</p>
                        <div className="subtle mono">{portfolio.technicalName}</div>
                    </div>
                    <div className="action-row"><button className="button secondary" onClick={onBack}>← Portfolios</button></div>
                </div>
                <div className="stat-grid">
                    <Stat label="YTD P&L" value={signed(portfolio.netPnl)} className={metricClass(portfolio.netPnl)}/>
                    <Stat label="Total risk" value={money(portfolio.totalRiskUsd)}/>
                    <Stat label="Strategies" value={portfolio.strategyCount}/>
                </div>
            </section>
            <section className="section"><PnlCharts dailyPnl={portfolio.dailyPnl}/></section>
            <section className="section panel">
                <div className="panel-head section-head">
                    <div><h2>Strategies</h2></div>
                    <div className="subtle">
                        {portfolio.strategyCount}
                        {" "}
                        contributors
                    </div>
                </div>
                <ContributorsTable contributors={portfolio.contributors}/>
            </section>
            <TradesTable trades={portfolio.trades} showStrategy subtitle={`${portfolio.window} · closed and open trades`}/>
            {portfolio.warnings?.length ? (
                <section className="section panel">
                    <div className="panel-head">
                        <div className="eyebrow">Warnings</div>
                        <h2>What needs attention</h2>
                    </div>
                    <div className="timeline">
                        {portfolio.warnings.map((warning) => (
                            <div className="timeline-item" key={warning}>
                                <div className="warning">Attention</div>
                                <div>{warning}</div>
                            </div>
                        ))}
                    </div>
                </section>
            ) : null}
            <div className="footnote">
                Last snapshot:
                {" "}{formatDateTime(portfolio.asOf)}
            </div>
        </>
    );
}
